import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { generateThemeVariables } from '@/utils/colorUtils';

export interface CustomTheme {
  id: string;
  name: string;
  primaryColor: string;
  secondaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderWidth?: number;
  radius: number;
}

export type Theme = 'default' | 'neo-brutalist' | 'luxury-dark' | 'playful-pastel' | string;
export type Mode = 'dark' | 'light' | 'system';

interface ThemeProviderState {
  theme: Theme;
  mode: Mode;
  customThemes: CustomTheme[];
  setTheme: (theme: Theme) => void;
  setMode: (mode: Mode) => void;
  addCustomTheme: (theme: CustomTheme) => void;
  removeCustomTheme: (id: string) => void;
}

const initialState: ThemeProviderState = {
  theme: 'default',
  mode: 'system',
  customThemes: [],
  setTheme: () => null,
  setMode: () => null,
  addCustomTheme: () => null,
  removeCustomTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

/**
 * Theme fields are persisted by the user's browser (localStorage JSON), so a
 * tampered value would otherwise be interpolated straight into the generated
 * stylesheet. Anything non-numeric or out of the builder's range falls back to
 * the default instead of reaching CSS.
 */
const toCssNumber = (value: unknown, fallback: number, max: number): number => {
  if (value === null || value === undefined || value === '') return fallback;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 && num <= max ? num : fallback;
};

const BUILT_IN_THEMES = ['default', 'neo-brutalist', 'luxury-dark', 'playful-pastel'];
const isBuiltInTheme = (value: unknown): boolean => typeof value === 'string' && BUILT_IN_THEMES.includes(value);

export function ThemeProvider({
  children,
  defaultTheme = 'default',
  defaultMode = 'system',
  storageKey = 'vite-ui-theme',
  modeStorageKey = 'vite-ui-theme-mode',
  customThemesStorageKey = 'vite-ui-custom-themes',
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultMode?: Mode;
  storageKey?: string;
  modeStorageKey?: string;
  customThemesStorageKey?: string;
}) {

  const [theme, setTheme] = useState<Theme>(() => {
    // localStorage is user-editable: only accept a value we can act on.
    const stored = localStorage.getItem(storageKey);
    return isBuiltInTheme(stored) || (typeof stored === 'string' && stored.startsWith('custom-'))
      ? (stored as Theme)
      : defaultTheme;
  });

  const [mode, setMode] = useState<Mode>(() => {
    const stored = localStorage.getItem(modeStorageKey);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : defaultMode;
  });

  const [customThemes, setCustomThemes] = useState<CustomTheme[]>(() => {
    try {
      const stored = localStorage.getItem(customThemesStorageKey);
      if (!stored) return [];
      const parsed: unknown = JSON.parse(stored);
      // Reject a non-array / malformed payload instead of letting the wrong
      // shape reach generateThemeVariables.
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (t): t is CustomTheme =>
          Boolean(t) && typeof t === 'object' && typeof (t as CustomTheme).id === 'string',
      );
    } catch {
      return [];
    }
  });

  // Handle standard themes
  useEffect(() => {
    const root = window.document.documentElement;
    root.removeAttribute('data-theme');
    
    // Check if it's a built-in theme
    const isBuiltIn = isBuiltInTheme(theme);
    
    if (theme !== 'default' && isBuiltIn) {
      root.setAttribute('data-theme', theme);
    } else if (!isBuiltIn) {
      root.setAttribute('data-theme', 'custom');
    }
  }, [theme]);

  // Handle dynamic custom theme injection
  useEffect(() => {
    const isBuiltIn = isBuiltInTheme(theme);
    let styleTag = document.getElementById('custom-theme-styles');
    
    if (isBuiltIn) {
      if (styleTag) {
        styleTag.remove();
      }
      return;
    }

    const currentCustomTheme = customThemes.find(t => t.id === theme);
    if (!currentCustomTheme) {
      // The selected custom theme no longer exists (deleted in another tab /
      // cleared storage): drop the stale variables instead of leaving them.
      if (styleTag) styleTag.remove();
      return;
    }

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'custom-theme-styles';
      document.head.appendChild(styleTag);
    }

    const isDark = document.documentElement.classList.contains('dark');
    const variables = generateThemeVariables(
      currentCustomTheme.primaryColor, 
      toCssNumber(currentCustomTheme.radius, 0.5, 2), 
      isDark,
      currentCustomTheme.secondaryColor,
      currentCustomTheme.backgroundColor,
      currentCustomTheme.textColor,
      currentCustomTheme.borderColor,
      currentCustomTheme.borderWidth === undefined || currentCustomTheme.borderWidth === null
        ? undefined
        : toCssNumber(currentCustomTheme.borderWidth, 1, 4)
    );
    
    styleTag.textContent = `
      [data-theme='custom'] {
        ${Object.entries(variables).filter(([, value]) => value !== undefined).map(([key, value]) => `${key}: ${value};`).join('\n        ')}
      }
    `;
  }, [theme, mode, customThemes]); // Re-run when mode changes to update dark/light variables

  // Handle light/dark mode
  useEffect(() => {
    const root = window.document.documentElement;
    const apply = () => {
      root.classList.remove('light', 'dark');
      if (mode === 'system') {
        root.classList.add(
          window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        );
        return;
      }
      root.classList.add(mode);
    };

    apply();

    if (mode !== 'system') return;
    // Follow the OS while in 'system' mode, otherwise an OS theme switch left
    // the document stale until the mode was changed again.
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [mode]);

  const handleSetTheme = useCallback((newTheme: Theme) => {
    localStorage.setItem(storageKey, newTheme);
    setTheme(newTheme);
  }, [storageKey]);

  const handleSetMode = useCallback((newMode: Mode) => {
    localStorage.setItem(modeStorageKey, newMode);
    setMode(newMode);
  }, [modeStorageKey]);

  const handleAddCustomTheme = useCallback((newTheme: CustomTheme) => {
    setCustomThemes(prev => {
      const next = [...prev, newTheme];
      localStorage.setItem(customThemesStorageKey, JSON.stringify(next));
      return next;
    });
  }, [customThemesStorageKey]);

  const handleRemoveCustomTheme = useCallback((id: string) => {
    setCustomThemes(prev => {
      const next = prev.filter(t => t.id !== id);
      localStorage.setItem(customThemesStorageKey, JSON.stringify(next));
      return next;
    });
    if (theme === id) {
      setTheme('default');
      localStorage.setItem(storageKey, 'default');
    }
  }, [customThemesStorageKey, storageKey, theme]);

  const value = useMemo(() => ({
    theme,
    mode,
    customThemes,
    setTheme: handleSetTheme,
    setMode: handleSetMode,
    addCustomTheme: handleAddCustomTheme,
    removeCustomTheme: handleRemoveCustomTheme,
  }), [theme, mode, customThemes, handleSetTheme, handleSetMode, handleAddCustomTheme, handleRemoveCustomTheme]);

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
